import {
  DateRangeFilterValue,
  FilterPickerType,
  FilterSelectedValue,
  GroupByTypeEnum,
} from "../../types";
import { IBoardFilterValues, IBoardsFilters } from "../Settings/types";
import { isDateRangeValue } from "./filterDropdownHelpers";

export const FILTER_ID_TO_VALUES_KEY: Partial<
  Record<GroupByTypeEnum, keyof IBoardFilterValues>
> = {
  [GroupByTypeEnum.TICKET_TYPE]: "ticketType",
  [GroupByTypeEnum.STATUS]: "status",
  [GroupByTypeEnum.TAGS]: "tags",
  [GroupByTypeEnum.DUE_DATE]: "dueDate",
  [GroupByTypeEnum.PRIORITY]: "priority",
  [GroupByTypeEnum.RELEASE]: "release",
  [GroupByTypeEnum.TARGET_RELEASE]: "targetRelease",
  [GroupByTypeEnum.SPRINT]: "sprint",
  [GroupByTypeEnum.ENVIRONMENT]: "environment",
  [GroupByTypeEnum.SEVERITY]: "severity",
  [GroupByTypeEnum.COMPONENT]: "component",
  [GroupByTypeEnum.ASSIGNEE]: "assignee",
  [GroupByTypeEnum.CREATOR]: "creator",
  [GroupByTypeEnum.CREATED_DATE]: "createdDate",
};

const FILTER_VALUES_KEY_TO_ID = Object.fromEntries(
  Object.entries(FILTER_ID_TO_VALUES_KEY).map(([filterId, key]) => [
    key,
    filterId,
  ]),
) as Record<keyof IBoardFilterValues, GroupByTypeEnum>;

export const getFilterValuesKey = (
  filterId: string,
): keyof IBoardFilterValues | undefined =>
  FILTER_ID_TO_VALUES_KEY[filterId as GroupByTypeEnum];

export const filterValueToStored = (
  value: FilterSelectedValue,
  pickerType: FilterPickerType,
): string[] => {
  if (pickerType === FilterPickerType.DATE_RANGE && isDateRangeValue(value)) {
    const stored: string[] = [];
    if (value.startDate) stored.push(value.startDate);
    if (value.endDate) stored.push(value.endDate);
    return stored;
  }

  return Array.isArray(value) ? value : [];
};

export const storedToFilterValue = (
  stored: string[] | undefined,
  pickerType: FilterPickerType,
): FilterSelectedValue => {
  if (pickerType === FilterPickerType.DATE_RANGE) {
    return {
      startDate: stored?.[0] ?? null,
      endDate: stored?.[1] ?? null,
    } satisfies DateRangeFilterValue;
  }

  return stored ?? [];
};

export const deriveAppliedFilterIds = (
  filterValues: IBoardFilterValues | null | undefined,
): string[] => {
  if (!filterValues) return [];

  return (Object.keys(FILTER_VALUES_KEY_TO_ID) as (keyof IBoardFilterValues)[])
    .filter((key) => (filterValues[key]?.length ?? 0) > 0)
    .map((key) => FILTER_VALUES_KEY_TO_ID[key]);
};

export const getAppliedFilterIds = (boardFilters: IBoardsFilters): string[] => {
  if (boardFilters.appliedFilterIds?.length) {
    return boardFilters.appliedFilterIds;
  }

  return deriveAppliedFilterIds(boardFilters.filters);
};

export const buildSelectedOptionsMap = (
  appliedFilterIds: string[],
  filterValues: IBoardFilterValues | null | undefined,
  getPickerType: (filterId: string) => FilterPickerType,
): Record<string, FilterSelectedValue> => {
  const selectedOptions: Record<string, FilterSelectedValue> = {};

  appliedFilterIds.forEach((filterId) => {
    const valuesKey = getFilterValuesKey(filterId);
    if (!valuesKey) return;

    selectedOptions[filterId] = storedToFilterValue(
      filterValues?.[valuesKey],
      getPickerType(filterId),
    );
  });

  return selectedOptions;
};
